import BaseLayer from 'ol/layer/Base';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatHint, MatInput, MatLabel } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import MVT from 'ol/format/MVT';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile';
import { Circle } from 'ol/style';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import { debounceTime, filter, merge } from 'rxjs';
import { AppService } from '../../../../services/app.service';
import { MapService } from '../../../../services/map.service';
import { BaseUILayer } from '../../../classes/layers/base-ui-layer';
import { LayerHeaderComponent } from '../../layer-header/layer-header.component';

interface FilterGroupType {
  year: FormControl<number>,
  useWithConstruction: FormControl<boolean>;
  minCapacity: FormControl<number | null>;
  maxCapacity: FormControl<number | null>;
}

@Component({
  selector: 'tgrid-substation-headroom-layer',
  imports: [
    LayerHeaderComponent,
    FormsModule,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    ReactiveFormsModule,
    MatInput,
    MatHint,
  ],
  templateUrl: './substation-headroom-layer.component.html',
  styleUrl: './substation-headroom-layer.component.scss',
})
export class SubstationHeadroomLayerComponent extends BaseUILayer<BaseLayer> {
  private appService = inject(AppService);
  protected filterGroup: FormGroup<FilterGroupType> = new FormGroup({
    year: new FormControl(2020, { nonNullable: true }),
    useWithConstruction: new FormControl(true, { nonNullable: true }),
    minCapacity: new FormControl<number | null>(null),
    maxCapacity: new FormControl<number | null>(null),
  });


  constructor() {
    super();

    const states$ = toObservable(this.appService.selectedStates);
    merge(this.filterGroup.valueChanges, states$).pipe(
      filter(() => this.checked),
      debounceTime(250),
      takeUntilDestroyed(),
    ).subscribe(() => {
      const source = (this.mapLayer().renderedLayer as VectorTileLayer).getSource();
      source?.setUrl(this.buildUrl());
    });


  }


  private buildUrl(): string {
    const filters = this.filterGroup.getRawValue();
    const stateList = this.appService.selectedStates().map(s => s.abbreviation).join(',');

    let url = this.mapLayer().serviceUrl;
    url += `?state=${stateList || ''}`;
    url += `&year=${filters.year}`;
    url += `&compare-construction=${filters.useWithConstruction}`;
    if (filters.minCapacity !== null) {
      url += `&min-capacity=${filters.minCapacity}`;
    }
    return url;
  }

  override toggleLayer(checked: boolean): void {
    this.checked = checked;
    if (this.checked) {
      if (this.mapLayer().renderedLayer) {
        const source = (this.mapLayer().renderedLayer as VectorTileLayer).getSource() as VectorTileSource;
        source?.setUrl(this.buildUrl());
      } else {
        this.mapLayer().renderedLayer = new VectorTileLayer({
          declutter: true,
          renderBuffer: 200,
          properties: {
            type: this.mapLayer().serviceType,
            name: this.mapLayer().displayName,
            popoverFunction: this.mapLayer().createPopover.bind(this.mapLayer()),
          },
          source: new VectorTileSource({
            format: new MVT(),
            url: this.buildUrl(),
            attributions: this.mapLayer().buildAttribution(),
          }),
          style: (feature) => {
            const properties = feature.getProperties();
            const key: number = properties['mvaConstruction'];

            let color = '#444';
            if (key < 100) color = '#FF0000';
            else if (key >= 100 && key <= 500) color = '#FF8000';
            else if (key > 500) color = '#00FF00';

            return new Style({
              image: new Circle({
                fill: new Fill({
                  color,
                }),
                stroke: new Stroke({
                  color: 'black',
                  width: 2,
                }),
                radius: 4,
              }),
            });

          },


        });
      }

      MapService.getMapRef()!.addLayer(this.mapLayer().renderedLayer);
      this.changeOpacity(this.opacity);

    } else {
      MapService.getMapRef()!.removeLayer(this.mapLayer().renderedLayer);
    }
  }

  override changeOpacity(value: number) {
    this.opacity = value;
    (this.mapLayer().renderedLayer as VectorTileLayer).setOpacity(value / 100);
  }

  override onDestroy(): void {
  }

}
